import { createApi } from '@reduxjs/toolkit/query/react'
import { handleError } from '../../commons/utils/utils'
import { setCampaignCategoryInfo } from '../slices/campaignCategorySlice'
import { SHARED_TAGS } from '../slices/constants'
import { generateRPCQuery, getBaseQuery } from './apiUtils'
import apiConstants from './constants'

export const campaignCategoriesApi = createApi({
  reducerPath: 'campaignCategoriesApi',
  refetchOnFocus: false,
  baseQuery: getBaseQuery(),
  tagTypes: ['CampaignCategories', SHARED_TAGS.CAMPAIGN_CATEGORY_INFO],
  endpoints: (builder) => ({
    getCampaignCategories: builder.query({
      query: (requestParams: CommonSearchParams) =>
        generateRPCQuery(
          apiConstants.CAMPAIGN_CATEGORIES_RPC_PATH,
          'getCampaignCategories',
          requestParams
        ),
      providesTags: [{ type: 'CampaignCategories' }]
    }),
    getCampaignCategoryInfo: builder.query({
      query: (requestParams: GetCampaignCategoryInfoParams) =>
        generateRPCQuery(
          apiConstants.CAMPAIGN_CATEGORIES_RPC_PATH,
          'getCampaginCategoryInfoByID',
          requestParams
        ),
      providesTags: [SHARED_TAGS.CAMPAIGN_CATEGORY_INFO],
      async onQueryStarted ({}, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (data && data.result) {
            dispatch(setCampaignCategoryInfo(data.result))
          }
        } catch (err: any) {
          handleError(err, dispatch)
        }
      }
    }),
    validateCampaignCategoryFormFields: builder.mutation({
      query: (requestParams: CampaignCategoryFormFields) =>
        generateRPCQuery(
          apiConstants.CAMPAIGN_CATEGORIES_RPC_PATH,
          'validateCampaignCategoryFormFields',
          requestParams
        )
    }),
    createNewCampaignCategory: builder.mutation({
      query: (requestParams: CampaignCategoryFormFields) =>
        generateRPCQuery(
          apiConstants.CAMPAIGN_CATEGORIES_RPC_PATH,
          'createNewCampaignCategory',
          requestParams
        ),
      invalidatesTags: [{ type: 'CampaignCategories' }]
    }),
    editCampaignCategory: builder.mutation({
      query: (requestParams: CampaignCategoryFormFields) =>
        generateRPCQuery(
          apiConstants.CAMPAIGN_CATEGORIES_RPC_PATH,
          'editCampaignCategory',
          requestParams
        ),
      invalidatesTags: [{ type: 'CampaignCategoryInfo' }]
    }),
    updateCategoryUiPositions: builder.mutation({
      query: (requestParams: CategoryUpdateUiParams) =>
        generateRPCQuery(
          apiConstants.CAMPAIGN_CATEGORIES_RPC_PATH,
          'updateUiPositions',
          requestParams
        ),
      invalidatesTags: [{ type: 'CampaignCategories' }]
    }),
    softDeleteCampaignCategory: builder.mutation({
      query: (requestParams: { CampaignCategoryID: number }) =>
        generateRPCQuery(
          apiConstants.CAMPAIGN_CATEGORIES_RPC_PATH,
          'softDeleteCampaignCategory',
          requestParams
        ),
      invalidatesTags: [{ type: 'CampaignCategories' }]
    })
  })
})

export const {
  useValidateCampaignCategoryFormFieldsMutation,
  useCreateNewCampaignCategoryMutation,
  useEditCampaignCategoryMutation,
  useUpdateCategoryUiPositionsMutation,
  useGetCampaignCategoriesQuery,
  useGetCampaignCategoryInfoQuery,
  useSoftDeleteCampaignCategoryMutation
} = campaignCategoriesApi
